import React from 'react';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { AlertCircle } from 'lucide-react';

interface OccupancyTypeSelectorProps {
  value: 'primary' | 'investment' | 'second';
  onChange: (value: 'primary' | 'investment' | 'second') => void;
  investorAdditionalPoints: number;
}

const OccupancyTypeSelector: React.FC<OccupancyTypeSelectorProps> = ({ 
  value, 
  onChange, 
  investorAdditionalPoints 
}) => {
  const occupancyTypes = [
    {
      value: 'primary' as const,
      label: 'Primary Residence',
      description: 'Your main home'
    },
    {
      value: 'investment' as const,
      label: 'Investment Property',
      description: 'Rental or investment'
    },
    {
      value: 'second' as const,
      label: '2nd Home',
      description: 'Vacation home'
    }
  ];

  return (
    <div className="space-y-3">
      <Label className="text-base font-semibold">Occupancy Type (Conventional Only)</Label>
      
      <RadioGroup value={value} onValueChange={onChange} className="grid grid-cols-3 gap-3">
        {occupancyTypes.map((type) => (
          <label
            key={type.value}
            className={`flex items-center space-x-2 border rounded-lg p-3 cursor-pointer transition-all ${
              value === type.value 
                ? 'border-primary bg-primary/5 shadow-sm' 
                : 'border-border hover:border-primary/50 hover:bg-accent/50'
            }`}
          >
            <RadioGroupItem value={type.value} id={`occupancy-${type.value}`} />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm">{type.label}</div>
              <div className="text-xs text-muted-foreground">{type.description}</div>
            </div>
          </label>
        ))}
      </RadioGroup>

      {value !== 'primary' && (
        <div className="p-4 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="space-y-2 text-sm">
              <p className="font-medium text-amber-900 dark:text-amber-100">
                Investor/2nd Home Property Adjustment
              </p>
              <p className="text-amber-700 dark:text-amber-300">
                Additional <span className="font-bold">{investorAdditionalPoints}</span> points added to all scenarios.
                This is based on typical investor pricing with 25% down payment and 740 credit score.
              </p>
              <div className="text-xs text-amber-600 dark:text-amber-400 space-y-1 mt-2">
                <p className="font-medium">Actual investor pricing varies by:</p>
                <ul className="list-disc list-inside ml-2 space-y-0.5">
                  <li>Credit score (lower score = higher cost)</li>
                  <li>Down payment percentage (lower down = higher cost)</li>
                  <li>Loan amount</li>
                  <li>Occupancy type (investment vs. 2nd home)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OccupancyTypeSelector;
